import { parseSupportedLanguageFromLocation } from 'common/src/vtj/browser/url-utils'
import { initActionCreator } from 'common/src/vtj/mobx/action-creator'
import { configure, observable } from 'mobx'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'
import * as Common from 'todistukset-ui/src/vtj/stores/common.mobx.store'
import * as Dialogs from 'todistukset-ui/src/vtj/stores/dialogs.mobx.store'
import type { Serialized } from 'common/src/vtj/types/util.type'
import type { TodistuksetPriceList } from 'todistukset-common/src/vtj/types/price-list.type'
import type {
  TodistusOrder,
  TodistusOrderOverview,
} from 'todistukset-common/src/vtj/types/todistus-order.type'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import supportedPageLanguages from 'todistukset-ui/src/vtj/page-lang-options'
import { createAetTodistuspyynnotStore } from 'todistukset-ui/src/vtj/stores/aet-todistuspyynnot.store'
import { createAetOrdererFormStore } from 'todistukset-ui/src/vtj/stores/aet-orderer-form.store'
import { createAetSignerFormStore } from 'todistukset-ui/src/vtj/stores/aet-signer-form.store'
import type { DialogItem } from 'todistukset-ui/src/vtj/stores/dialogs.mobx.store'
import { createFormStore } from 'todistukset-ui/src/vtj/stores/form.mobx.store'
import { createOrderStatus } from 'todistukset-ui/src/vtj/stores/order.mobx.store'
import {
  priceListStore,
  type PriceListStore,
} from 'todistukset-ui/src/vtj/stores/price-list.store'
import {
  createStepsStore,
  type StepsStore,
} from 'todistukset-ui/src/vtj/stores/steps.mobx.store'
import { createAetTodistuspyyntoStore } from 'todistukset-ui/src/vtj/stores/aet-todistuspyynto.store'
import { TodistusPyyntöAetOverview } from 'todistukset-common/src/vtj/types/todistus'
import {
  AetSignerUiForm,
  TodistusStoreForm,
} from 'todistukset-common/src/vtj/types/form-types.type'
import { createUaetFormStore } from 'todistukset-ui/src/vtj/stores/uaet-form.store'
import { langIsTodistusasiointikieli } from 'todistukset-common/src/vtj/language.util'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { TodistusAsiointikieli } from 'todistukset-common/src/vtj/types/todistus-person.type'
import {
  DocumentDownloadStatusStore,
  createDocumentDownloadStatusStore,
} from 'todistukset-ui/src/vtj/stores/document-status.store'

configure({ enforceActions: 'observed' })

type SupportedUiLanguageCode = Parameters<
  typeof parseSupportedLanguageFromLocation
>[0][0]

type InitialState = {
  common?: TodistuksetUi.Store.InitCommon
  paymentPriceList?: Serialized<TodistuksetPriceList> | null
  priceList?: PriceListStore
  previousOrders?: Serialized<TodistusOrder>[]
  aetOrders?: Serialized<TodistusPyyntöAetOverview>[]
  uaetOrders?: Serialized<TodistusOrderOverview>[]
  dialogs?: DialogItem[]
  forms?: Record<CertificateTypes, TodistusStoreForm>
  steps?: StepsStore
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  order?: any
  errors?: string[]
  lang?: string
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  testUsers?: any[]
  aetSignerForms?: Record<number, AetSignerUiForm>
  documentDownloadStatus?: DocumentDownloadStatusStore
}

export const init = ({
  initialState = {},
  location,
}: {
  initialState?: InitialState
  location?: Location | string
} = {}): TodistuksetUi.Store => {
  const {
    common = {},
    previousOrders = [],
    aetOrders = [],
    uaetOrders = [],
    errors = [],
    testUsers = [],
    aetSignerForms = {},
  } = initialState

  const paymentPriceList = initialState.paymentPriceList ?? {}

  const langOverride =
    initialState &&
    (supportedPageLanguages() as unknown[]).includes(initialState.lang)
      ? (initialState.lang as SupportedUiLanguageCode)
      : undefined
  const lang =
    langOverride ||
    parseSupportedLanguageFromLocation(supportedPageLanguages())(
      String(location)
    )

  const createFormStoreHelper = (
    user: TodistuksetUser,
    todistusType: CertificateTypes,
    asiointikieli: TodistusAsiointikieli
  ) => {
    return createFormStore({
      user,
      asiointikieli,
      todistusType,
    })
  }

  const commonStore = Common.init({ ...common, lang })
  const { lang: commonStoreLang } = commonStore

  const asiointikieli: TodistusAsiointikieli = langIsTodistusasiointikieli(
    commonStoreLang
  )
    ? commonStoreLang
    : LanguageCode.FI

  const store = {
    common: commonStore,
    steps: (common.user &&
      createStepsStore(common.user)) as TodistuksetUi.Store['steps'],
    forms: (common.user && {
      [CertificateTypes.ASUINPAIKKATODISTUS]: createFormStoreHelper(
        common.user,
        CertificateTypes.ASUINPAIKKATODISTUS,
        asiointikieli
      ),
      [CertificateTypes.ELOSSAOLOTODISTUS]: createFormStoreHelper(
        common.user,
        CertificateTypes.ELOSSAOLOTODISTUS,
        asiointikieli
      ),
      [CertificateTypes.SIVIILISAATYTODISTUS]: createFormStoreHelper(
        common.user,
        CertificateTypes.SIVIILISAATYTODISTUS,
        asiointikieli
      ),
      [CertificateTypes.AVIOLIITTOTODISTUS]: createFormStoreHelper(
        common.user,
        CertificateTypes.AVIOLIITTOTODISTUS,
        asiointikieli
      ),
      [CertificateTypes.SYNTYMATODISTUS]: createFormStoreHelper(
        common.user,
        CertificateTypes.SYNTYMATODISTUS,
        asiointikieli
      ),
    }) as TodistuksetUi.Store['forms'],
    aetStore: createAetTodistuspyynnotStore(aetOrders),
    aetOrdererForm: createAetOrdererFormStore(common.user, asiointikieli),
    aetSignerForms: observable(aetSignerForms),
    aetSignerFormFn: (requestId: number) => {
      if (aetSignerForms[requestId]) {
        return aetSignerForms[requestId]
      } else {
        const form = createAetSignerFormStore(common.user, asiointikieli)
        aetSignerForms[requestId] = form
        return form
      }
    },
    aetTodistuspyyntoBannerStore: createAetTodistuspyyntoStore(),
    uaetOrders: observable(uaetOrders),
    uaetForm: createUaetFormStore(common.user, asiointikieli),
    order: createOrderStatus(),
    previousOrders: observable(previousOrders),
    paymentPriceList: observable(
      paymentPriceList
    ) as TodistuksetUi.Store['paymentPriceList'],
    dialogs: Dialogs.init(),
    errors: observable(errors),
    testUsers: observable(testUsers),
    priceList: priceListStore,
    documentDownloadStatus: createDocumentDownloadStatusStore(),
  }

  initActionCreator(store)

  return store
}
